import type { AppDispatch } from '@store/index'
import { addNote, updateNote, removeNote } from '@store/actions/note.action'
import { INote, INoteTranscriptSegment } from '@dataTypes/note.type'
import { NoteContextType } from '@components/common/TagBadge/TagBadge.component'
import { getNoteById, getAllNotes } from '@services/api/note.api'
import { isLatestNoteInVersionChain } from '@utils/noteVersion.util'

/** Payload shape used by NoteEditor when saving (add or update). */
export interface SaveNotePayload {
  id?: string
  title: string
  content: string
  tag: string
  transcript_segments?: INoteTranscriptSegment[] | null
}

/** Parameters required to perform save (add/update) from context (e.g. Charting, Message Center contact). */
export interface SaveNoteParams {
  noteData: SaveNotePayload
  /** Required for patient notes; omit for contact-only notes (e.g. provider). */
  patientId?: string
  /** Required for contact-only notes; can be used with patientId for contact context. */
  contactId?: string
  contextId?: string | null
  contextType: NoteContextType
  /** Fallback context type when noteData.tag is not set. */
  defaultContextType?: NoteContextType
  /**
   * All notes visible for this patient/contact used to resolve version chains.
   * When omitted, a notes list is fetched from the API when possible.
   */
  siblingNotes?: INote[]
}

export interface NoteActionCallbacks {
  onSuccess?: (title: string, message: string) => void
  onError?: (title: string, message: string) => void
}

async function resolveSiblingPool(params: SaveNoteParams): Promise<INote[]> {
  const { siblingNotes, patientId, contactId, contextId, contextType } = params
  if (siblingNotes && siblingNotes.length > 0) {
    return siblingNotes
  }
  try {
    const res = await getAllNotes({
      patientId,
      contactId,
      contextId: contextId ?? undefined,
      contextType,
    })
    if (res.statusCode === 200 && Array.isArray(res.data?.notes)) {
      return res.data.notes
    }
  } catch {
    // handled below
  }
  return []
}

/**
 * Save a note (add if no id; if id present, create a new version row instead of updating in place).
 * Dispatches Redux actions. Returns the saved (newest) note on success, undefined on error.
 */
export async function saveNote(
  dispatch: AppDispatch,
  params: SaveNoteParams,
  callbacks?: NoteActionCallbacks,
): Promise<INote | undefined> {
  const {
    noteData,
    patientId,
    contactId,
    contextId,
    contextType,
    defaultContextType,
  } = params
  const effectiveContextType =
    (noteData.tag as NoteContextType) || defaultContextType || contextType
  const contextPayload =
    contextId != null && contextId.trim() !== ''
      ? { context_id: contextId }
      : {}

  const isCallOnly =
    effectiveContextType === NoteContextType.CALL &&
    contextId != null &&
    contextId.trim() !== ''
  const hasRequiredOwner = Boolean(patientId || contactId || isCallOnly)

  if (!noteData.id && !hasRequiredOwner) {
    callbacks?.onError?.(
      'Save note failed',
      'Either patient or contact is required to save a note.',
    )
    return undefined
  }

  try {
    if (noteData.id) {
      const pool = await resolveSiblingPool(params)
      const existingRes = await getNoteById(noteData.id)
      if (existingRes.statusCode !== 200 || !existingRes.data?.note) {
        callbacks?.onError?.(
          'Save note failed',
          'Could not load the note to save. Please try again.',
        )
        return undefined
      }
      const existing = existingRes.data.note
      const existingContextPayload =
        existing.context_id != null && existing.context_id.trim() !== ''
          ? { context_id: existing.context_id }
          : contextPayload
      const versionContextType = existing.context_type ?? effectiveContextType

      if (!isLatestNoteInVersionChain(existing, pool)) {
        callbacks?.onError?.(
          'Save note failed',
          'Only the latest version of a note can be edited.',
        )
        return undefined
      }

      const savedNote = await dispatch(
        updateNote({
          noteId: existing.note_id,
          updateData: {
            title: noteData.title.trim(),
            body: noteData.content,
            context_type: versionContextType,
            ...existingContextPayload,
            ...(noteData.transcript_segments !== undefined
              ? { transcript_segments: noteData.transcript_segments }
              : {}),
          },
        }),
      ).unwrap()
      callbacks?.onSuccess?.('Success', 'New note version saved successfully')
      return savedNote as INote
    }
    const savedNote = await dispatch(
      addNote({
        ...(patientId ? { patient_id: patientId } : {}),
        ...(contactId ? { contact_id: contactId } : {}),
        title: noteData.title,
        body: noteData.content,
        context_type: effectiveContextType,
        ...contextPayload,
        ...(noteData.transcript_segments !== undefined
          ? { transcript_segments: noteData.transcript_segments }
          : {}),
      }),
    ).unwrap()
    callbacks?.onSuccess?.('Success', 'Note saved successfully')
    return savedNote as INote
  } catch {
    callbacks?.onError?.(
      'Save note failed',
      'Failed to save note. Please try again.',
    )
    return undefined
  }
}

/**
 * Delete a note by id. Dispatches Redux removeNote action.
 */
export async function deleteNote(
  dispatch: AppDispatch,
  noteId: string,
  callbacks?: NoteActionCallbacks,
): Promise<void> {
  try {
    await dispatch(removeNote(noteId)).unwrap()
    callbacks?.onSuccess?.('Success', 'Note deleted successfully')
  } catch {
    callbacks?.onError?.(
      'Delete note failed',
      'Failed to delete note. Please try again.',
    )
  }
}
